import React, { useState, useEffect, useRef } from 'react';
import { 
    X, CreditCard, Lock, AlertCircle, CheckCircle, 
    Loader, Shield, Info
} from 'lucide-react';

const SubscriptionCheckout = ({ plan, isOpen, onClose, onSuccess }) => {
    const [squarePayments, setSquarePayments] = useState(null);
    const [card, setCard] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [cardErrors, setCardErrors] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [nameOnCard, setNameOnCard] = useState('');
    const cardContainerRef = useRef(null);

    // Get environment configuration
    const environment = import.meta.env.VITE_SQUARE_ENVIRONMENT;
    const applicationId = import.meta.env.VITE_SQUARE_APPLICATION_ID;
    const locationId = import.meta.env.VITE_SQUARE_LOCATION_ID;

    useEffect(() => {
        if (isOpen && !squarePayments) {
            loadSquareSDK();
        }
    }, [isOpen]);

    useEffect(() => {
        if (squarePayments && isOpen && !card) {
            initializeCard();
        }
    }, [squarePayments, isOpen]);

    const loadSquareSDK = async () => {
        if (window.Square) {
            initializeSquare();
            return;
        }

        setIsLoading(true);
        try {
            const scriptSrc = environment === 'production' 
                ? 'https://web.squarecdn.com/v1/square.js'
                : 'https://sandbox.web.squarecdn.com/v1/square.js';

            const script = document.createElement('script');
            script.src = scriptSrc;
            script.async = true;
            script.onload = initializeSquare;
            script.onerror = () => {
                setError('Failed to load payment system. Please refresh and try again.');
                setIsLoading(false);
            };
            document.head.appendChild(script);
        } catch (err) {
            setError('Failed to initialize payment system');
            setIsLoading(false);
        }
    };

    const initializeSquare = async () => {
        try {
            if (!window.Square) {
                throw new Error('Square SDK not loaded');
            }

            // Initialize Square payments
            const payments = window.Square.payments(applicationId, locationId);
            setSquarePayments(payments);
            
        } catch (err) {
            console.error('Error initializing Square:', err);
            setError('Failed to initialize payment form. Please refresh and try again.');
            setIsLoading(false);
        }
    };

    const initializeCard = async () => {
        try {
            if (!squarePayments || !cardContainerRef.current) {
                return;
            }

            // Give the SDK a moment to finish internal setup after payments() is called
            await new Promise(resolve => setTimeout(resolve, 150));

            if (!cardContainerRef.current) return;

            // Initialize card payment method
            const cardOptions = {
                style: {
                    input: {
                        color: '#ffffff',
                        fontSize: '16px',
                        backgroundColor: '#1f2937',
                    },
                    'input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.35)',
                    },
                    '.input-container': {
                        borderColor: '#374151',
                        borderRadius: '8px',
                    },
                    '.input-container.is-focus': {
                        borderColor: '#3b82f6',
                    },
                    '.input-container.is-error': {
                        borderColor: '#ef4444',
                    }
                }
            };

            const cardInstance = await squarePayments.card(cardOptions);
            await cardInstance.attach(cardContainerRef.current);
            setCard(cardInstance);
            
            // Handle real-time validation
            cardInstance.addEventListener('cardBrandChanged', (event) => {
                // Handle card brand change if needed
            });

            cardInstance.addEventListener('errorClassAdded', (event) => {
                setCardErrors(prev => ({ ...prev, [event.field]: event.message }));
            });

            cardInstance.addEventListener('errorClassRemoved', (event) => {
                setCardErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[event.field];
                    return newErrors;
                });
            });

        } catch (err) {
            console.error('Error initializing card:', err);
            setError('Failed to initialize payment form. Please refresh and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePayment = async (e) => {
        e.preventDefault();

        if (!nameOnCard.trim()) {
            setError('Please enter the name as it appears on your card.');
            return;
        }

        if (!card || !squarePayments) {
            setError('Payment system not ready. Please try again.');
            return;
        }

        setIsProcessing(true);
        setError('');

        try {
            // Request payment token from Square
            const tokenResult = await card.tokenize();
            
            if (tokenResult.status === 'OK') {
                // Send payment token to backend
                const response = await fetch(`${import.meta.env.VITE_API_URL}/payments/create-subscription`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('deepwave_token')}`
                    },
                    body: JSON.stringify({
                        token: tokenResult.token,
                        planId: plan.id,
                        environment: environment,
                        nameOnCard: nameOnCard.trim()
                    })
                });

                const result = await response.json();

                if (result.success) {
                    // Payment successful
                    onSuccess?.(result);
                } else {
                    setError(result.message || 'Payment failed. Please try again.');
                }
            } else {
                // Handle tokenization errors
                const errors = tokenResult.errors || [];
                if (errors.length > 0) {
                    setError(errors[0].message || 'Payment validation failed');
                } else {
                    setError('Payment validation failed. Please check your card details.');
                }
            }
        } catch (err) {
            console.error('Payment error:', err);
            setError('Payment failed. Please try again or contact support.');
        } finally {
            setIsProcessing(false);
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount / 100);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
            <div className="glass-card max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-white">Complete Your Purchase</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white"
                        disabled={isProcessing}
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Plan Summary */}
                <div className="mb-6 p-4 bg-blue-600/20 rounded-lg">
                    <h4 className="text-lg font-semibold text-white mb-2">{plan.name}</h4>
                    <p className="text-blue-400 text-sm mb-3">{plan.description}</p>
                    <div className="flex justify-between items-center">
                        <span className="text-gray-300">Total:</span>
                        <span className="text-xl font-bold text-white">
                            {formatCurrency(plan.price, plan.currency)}
                            <span className="text-sm text-gray-400">
                                /{plan.billing_interval === 'YEARLY' ? 'year' : 'mo'}
                            </span>
                        </span>
                    </div>
                </div>

                {/* Security Notice */}
                <div className="mb-6 flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg">
                    <Shield className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                        <p className="text-green-400 text-sm font-medium">Secure Payment</p>
                        <p className="text-gray-400 text-xs">
                            Your payment information is encrypted and processed securely by Square.
                        </p>
                    </div>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="mb-4 p-3 bg-red-600/20 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                        <p className="text-red-400 text-sm">{error}</p>
                    </div>
                )}

                {/* Payment Form */}
                <form onSubmit={handlePayment} className="space-y-4">
                    {/* Name on Card */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Name on Card
                        </label>
                        <input
                            type="text"
                            value={nameOnCard}
                            onChange={(e) => setNameOnCard(e.target.value)}
                            placeholder="Full name as it appears on your card"
                            autoComplete="cc-name"
                            className="w-full px-3 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-white/35 focus:outline-none focus:border-blue-500 transition-colors"
                            disabled={isProcessing}
                        />
                    </div>

                    {/* Card Input Container */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Card Information
                        </label>
                        
                        <div 
                            ref={cardContainerRef}
                            className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg min-h-[48px] relative"
                        >
                            {isLoading && (
                                <div className="absolute inset-0 bg-gray-800/90 rounded-lg flex items-center justify-center z-10">
                                    <Loader className="w-5 h-5 text-blue-400 animate-spin" />
                                    <span className="ml-2 text-gray-400">Loading payment form...</span>
                                </div>
                            )}
                        </div>
                        
                        {/* Card validation errors */}
                        {Object.keys(cardErrors).length > 0 && (
                            <div className="mt-2 space-y-1">
                                {Object.entries(cardErrors).map(([field, message]) => (
                                    <p key={field} className="text-red-400 text-sm">
                                        {message}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Subscription Terms */}
                    <div className="p-3 bg-gray-800/50 rounded-lg">
                        <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 text-blue-400 mt-0.5" />
                            <div className="text-xs text-gray-400">
                                <p>• Your subscription will renew automatically</p>
                                <p>• You can cancel anytime from your account settings</p>
                                <p>• You'll receive an email receipt after payment</p>
                                <p>• Changes take effect immediately</p>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4 border-t border-gray-700">
                        <button
                            type="submit"
                            disabled={isLoading || isProcessing || !card}
                            className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isProcessing ? (
                                <>
                                    <Loader className="w-5 h-5 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <Lock className="w-5 h-5" />
                                    Support · {formatCurrency(plan.price, plan.currency)}/{plan.billing_interval === 'YEARLY' ? 'year' : 'mo'}
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Environment indicator for testing */}
                {environment === 'sandbox' && (
                    <div className="mt-4 p-2 bg-yellow-600/20 rounded-lg">
                        <p className="text-yellow-400 text-xs text-center">
                            ⚠️ Test Mode - Use card 4111 1111 1111 1111 for testing
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionCheckout;